import { useEffect, useState } from 'react';
import { getApiData } from 'lib/api/utils';
import { setNewSignIn } from './switch';

/** GET Account/sign-in-settings (anonymous). */
export type TSignInSettings = {
  newSignIn: boolean;
  googleClientId: string;
  maxPasswordTries: number;
  pauseMinutes: number;
  codeMinutes: number;
  resendSeconds: number;
  resetLinkMinutes: number;
  inviteDays: number;
  keepSignedInDays: number;
  rememberDeviceDays: number;
};

const OFF: TSignInSettings = {
  newSignIn: false,
  googleClientId: '',
  maxPasswordTries: 3,
  pauseMinutes: 15,
  codeMinutes: 10,
  resendSeconds: 30,
  resetLinkMinutes: 60,
  inviteDays: 7,
  keepSignedInDays: 30,
  rememberDeviceDays: 30,
};

let cached: TSignInSettings | null = null;
let pending: Promise<TSignInSettings> | null = null;

/** Asks once per page load; a failed call counts as switched off. */
export const loadSignInSettings = (): Promise<TSignInSettings> => {
  if (cached) {
    return Promise.resolve(cached);
  }
  if (!pending) {
    pending = getApiData('Account/sign-in-settings')
      .then((answer: { item?: Partial<TSignInSettings> } | string) => {
        const item = typeof answer === 'object' ? answer?.item : null;
        return item ? { ...OFF, ...item, newSignIn: item.newSignIn === true } : OFF;
      })
      .catch(() => OFF)
      .then((settings: TSignInSettings) => {
        cached = settings;
        setNewSignIn(settings.newSignIn);
        return settings;
      });
  }
  return pending;
};

/** The settings, or null while they load. */
export const useSignInSettings = (): TSignInSettings | null => {
  const [settings, setSettings] = useState<TSignInSettings | null>(cached);
  useEffect(() => {
    let live = true;
    if (!settings) {
      loadSignInSettings().then((loaded) => live && setSettings(loaded));
    }
    return () => {
      live = false;
    };
  }, [settings]);
  return settings;
};

/** Tests only: forget the cached answer. */
export const forgetSignInSettings = (): void => {
  cached = null;
  pending = null;
  setNewSignIn(false);
};
